import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useTable, NumberField } from "@refinedev/antd";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CatalogColumnHeaderCell } from "@/pages/catalog/CatalogColumnHeaderCell";
import { useColumnOrder } from "@/pages/quotes/utils/useColumnOrder";
import {
  Input,
  Table,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Flex,
  Typography,
  Badge,
  Select,
  notification,
  Button,
  message,
  Tag,
  Space,
  Tooltip,
  Modal,
  Radio,
  Divider,
} from "antd";
import { CrudFilters, HttpError, useOne } from "@refinedev/core";
import { ProductsFilters } from "@/pages/sellers/products/productsFilters";
import { TableRowSelection } from "antd/es/table/interface";
import {
  ApiOutlined,
  CloudSyncOutlined,
} from "@ant-design/icons";
import { ExcelIcon } from "@/components/icon/ExcelIcon";
import { LateralModal } from "@/components/modals/LateralModal";
import { PaginationTotal } from "@/components/pagination-total";
import { useFormatLastUpdate } from "@/utilities/date";
import dayjs from "dayjs";
import { TFunction, i18n as I18nType } from "i18next";
import { useTranslation } from "react-i18next";
import { FilterTags } from "@/components";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { ImportProducts } from "./products/importsProducts";
import { IntegrarErp } from "./products/integrarErp";
import {
  SugerenciaDeAsociacion,
  asociarConSugerencias,
  motivoSinAsociar,
  type ResultadoDeSugerencia,
  pedirSugerencias,
  sugerenciaActual,
} from "./products/sugerenciaDeAsociacion";
import type { EstadoDelAsociador } from "./products/asociadorPage";
import {
  filtrosDeMatch,
  opcionesDeMatch,
  tramoDelFiltro,
  type TramoDeMatch,
} from "./products/filtroDeMatch";
import { useNavigate, useParams } from "react-router-dom";
import { EmptyFormCard } from "@/components";
import {
  axiosInstance,
  filterUrlGenerate,
  isEqual,
  mapOperator,
} from "@/utilities";
import { API_URL_LINKED_SEARCH } from "@/services/XCONS_ENV";
import * as XLSX from "xlsx";
import { utils } from "xlsx";
import { Larrow } from "@/components/icon/Larrow";
import noImage from "@/assets/img/no-img.png";
import { OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Title, Text } = Typography;
const { Search } = Input;

/**
 * Las columnas de Productos asociados que se mueven con el asa (xcons-docs#630), en el orden en
 * que se declaran en `mainColumns`, sin la de desplegar. Son ids propios y no el `key`: varias
 * columnas comparten `key: "sku"`, y es lo que refine manda como orden a la API.
 */
const COLUMNAS_DE_PRODUCTOS = [
  "imagen",
  "marca",
  "sku",
  "nombreSeller",
  "productoXcons",
  "unidad",
  "precio",
  "stock",
  "plazo",
  "actualizado",
  "venta",
];

// Fuera del componente, como en Presupuestos: definida adentro AntD desmonta la celda en cada
// render y el arrastre se corta apenas empieza.
const COMPONENTES_DE_LA_TABLA = {
  header: {
    cell: (props: any) => <CatalogColumnHeaderCell {...props} handleInline />,
  },
};

export const SellersShow = () => {
  const [selectedRowOnTable, setSelectedRowOnTable] = useState<React.Key[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showSelectedOption, setShowSelectedOption] = useState<boolean>(false);
  const [totalFilterTag, setTotalFilterTag] = useState(0);
  const [dataEmpty, setDataEmpty] = useState<boolean>(false);
  const { smartMenuName } = useContext(SmartMenuContext);
  const [localData, setLocalData] = useState<any[]>([]);

  // ID del seller desde el LocalStorage
  const sellerCode = encodeURIComponent(
    smartMenuName?.replace(/"/g, "").toLowerCase(),
  );

  const navigate = useNavigate();
  const { id: sellerParam } = useParams<{ id: string }>();
  const screens = Grid.useBreakpoint();
  const formatLastUpdate = useFormatLastUpdate();
  const { token } = theme.useToken();
  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const {
      main_search,
      name_product,
      brands_product,
      manufacture_sku,
      catalog_sku,
      featured_product,
      price_product_from,
      price_product_to,
      status_product,
      status,
      match_tramo,
      match_desde,
      match_hasta,
      calendar,
      calendar_created,
      filters: existingFilters,
    } = params;

    if (existingFilters) {
      existingFilters.forEach((filter: any) => {
        filters.push(filter);
      });
    }

    filters.push(
      {
        field: "search",
        operator: "contains",
        value: main_search,
      },
      {
        field: "variations.enabled",
        operator: "eq",
        value: status_product,
      },
      {
        field: "name",
        operator: "contains",
        value: name_product,
      },
      {
        field: "manufacturer",
        operator: "contains",
        value: brands_product,
      },
      {
        field: "variations.mainCatalog.sku",
        operator: "contains",
        value: manufacture_sku,
      },
      {
        field: "sku",
        operator: "contains",
        value: catalog_sku,
      },
      {
        field: "featured",
        operator: "in",
        value: featured_product,
      },
      {
        field: "price",
        operator: "between",
        value:
          price_product_from !== undefined && price_product_to !== undefined
            ? [price_product_from, price_product_to]
            : price_product_from !== undefined
              ? [price_product_from, Number.MAX_VALUE]
              : price_product_to !== undefined
                ? [0, price_product_to]
                : undefined,
      },
      {
        field: "product.status",
        operator: "eq",
        value: status,
      },
      // Con el % guardado, sin calcular nada (xcons-docs#624).
      ...filtrosDeMatch(match_tramo, match_desde, match_hasta),
      {
        field: "updatedAt",
        operator: "between",
        value: calendar
          ? [dayjs(calendar[0])?.format(), dayjs(calendar[1]).format()]
          : undefined,
      },
      {
        field: "createdAt",
        operator: "between",
        value: calendar_created
          ? [
            dayjs(calendar_created[0])?.format(),
            dayjs(calendar_created[1]).format(),
          ]
          : undefined,
      },
    );

    setFilters(filters);
    return filters;
  };

  const {
    tableQuery: { data, isFetching, refetch: mainTableRefecth },
    searchFormProps,
    tableProps,
    filters,
    setFilters,
    current,
    setCurrent,
  } = useTable<HttpError>({
    resource: `sellers/linkedproducts/${sellerCode}`,
    meta: { refetch: true },
    pagination: {
      // Productos asociados arranca en 100, no en las 50 del resto (xcons-docs#601).
      pageSize: 100,
      mode: "server",
    },
    syncWithLocation: true,
    onSearch: onSearch,
  });

  const {
    data: configXlsData,
    isLoading: importLoading,
    refetch: refetchFormXls,
  } = useOne({
    resource: `sellers/linkedproducts/import/configuration`,
    id: sellerCode,
  });

  useEffect(() => {
    if (configXlsData && configXlsData.data) {
      const isEmptyData = data?.length === 0;
      if (isEmptyData) {
        setDataEmpty(true);
      } else {
        setDataEmpty(false);
      }
    }
  }, [configXlsData]);

  // Checkbox table
  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows: any[],
  ) => {
    setSelectedRowOnTable(selectedRows);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  useEffect(() => {
    setShowSelectedOption(selectedRowOnTable.length > 0);
  }, [selectedRowOnTable]);

  /** Todos los que trae el listado con sus filtros, no sólo la página que se ve. */
  const seleccionarTodos = async () => {
    if (!data?.total) return;
    try {
      message.loading({
        content: "Seleccionando todos los ítems...",
        key: "select-all",
      });
      const respuesta = await axiosInstance.get(
        `${API_URL_LINKED_SEARCH}/${sellerCode}?page=1&limit=${data?.total
        }&${filterUrlGenerate(filters)}`,
      );
      const allData = respuesta.data.data;
      if (allData) {
        setSelectedRowOnTable(allData);
        setSelectedRowKeys(allData.map((item: any) => item.sku));
        message.success({
          content: "Todos los ítems seleccionados",
          key: "select-all",
        });
      }
    } catch (error) {
      message.error({
        content: "Error al cargar la selección completa",
        key: "select-all",
      });
    }
  };

  /**
   * El link "Asociar" del aviso de un import (xcons-docs#624): cierra el panel, deja la tabla
   * en los no asociados de ese import con 80% o más, y cuando cargan los selecciona a todos.
   * Desde ahí se asocian con "Asociar con la sugerencia".
   */
  const [cierresDelImport, setCierresDelImport] = useState(0);
  const [seleccionarAlCargar, setSeleccionarAlCargar] = useState(false);
  const asociarCoincidencias = (importId: number) => {
    setCierresDelImport((n) => n + 1);
    setSelectedRowKeys([]);
    setSelectedRowOnTable([]);
    setFilters(
      [
        { field: "product.status", operator: "eq", value: "unlinked" },
        ...filtrosDeMatch("verde"),
        { field: "importId", operator: "eq", value: importId },
      ],
      "replace",
    );
    setCurrent(1);
    setSeleccionarAlCargar(true);
  };
  useEffect(() => {
    if (!seleccionarAlCargar || isFetching || !data) return;
    setSeleccionarAlCargar(false);
    seleccionarTodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccionarAlCargar, isFetching, data]);

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
    preserveSelectedRowKeys: true,
    selections: [
      {
        key: "all-data",
        text: `Seleccionar todos (${data?.total || 0})`,
        onSelect: seleccionarTodos,
      },
      {
        key: "clear-all",
        text: "Limpiar selección",
        onSelect: () => {
          setSelectedRowKeys([]);
          setSelectedRowOnTable([]);
        },
      },
    ],
  };

  const tblRef = React.useRef(null);

  const isLoadingTable = !data || isFetching;

  const dataSource = useMemo(
    () => (isLoadingTable ? [] : localData || []),
    [isLoadingTable, localData],
  );

  useEffect(() => {
    if (data?.data) {
      setLocalData(data.data);
    }
  }, [data?.data]);
  const mainColumns: TableColumnsType<any> = [
    {
      key: "variations",
      title: t("products.show.tableLinked"),
      dataIndex: "variations",
      sorter: (a, b) => {
        if (a.variations.length === b.variations.length) {
          return a.variations[0]?.mainCatalog?.name.localeCompare(
            b.variations[0]?.mainCatalog?.name,
          );
        }
        return a.variations.length - b.variations.length;
      },
      render: (_, record) => {
        // Sin "Catalogación solicitada" (xcons-docs#609): ahora se cataloga directo, así que
        // un producto pedido antes es uno sin asociar más.
        const notLinked = record?.variations.length === 0;

        return (
          <>
            {notLinked ? (
              <Flex
                gap={6}
                vertical
                align="center"
                justify="center"
                style={{ height: 80, width: 80 }}
              >
                <ApiOutlined
                  style={{ color: token.colorError, fontSize: "20px" }}
                />
                <Text
                  style={{
                    color: token.colorTextSecondary,
                    textAlign: "center",
                  }}
                >
                  {t("products.show.notLinked")}
                </Text>
              </Flex>
            ) : (
              <Flex gap={12} style={{ height: 80, width: 80 }}>
                <img
                  loading="lazy"
                  style={{ width: 80, height: 80, objectFit: "contain" }}
                  src={
                    record?.variations[0]?.mainCatalog?.image
                      ? record?.variations[0]?.mainCatalog?.image
                      : noImage
                  }
                  alt={record?.variations[0]?.mainCatalog?.name}
                />
              </Flex>
            )}
          </>
        );
      },
      shouldCellUpdate: (record, prevRecord) =>
        isEqual(prevRecord.key, record?.key),
    },
    Table.EXPAND_COLUMN,
    {
      key: "manufacturer",
      title: t("products.show.tableBrand"),
      dataIndex: "manufacturer",
      sorter: (a, b) =>
        a.manufacturer.toLowerCase().charCodeAt(0) -
        b.manufacturer.toLowerCase().charCodeAt(0),
      render: (manufacturer, record) => {
        return (
          <Flex gap={12} vertical style={{ minWidth: 130 }}>
            <Flex gap={5}>
              <Text style={{ color: token.colorTextSecondary }}>
                {manufacturer ?? "-"}
              </Text>
            </Flex>
            {record?.variations[0]?.mainCatalog?.manufacturer && (
              <Flex gap={5} align="start">
                <Text strong>
                  {" "}
                  <Larrow />
                  {record?.variations[0]?.mainCatalog?.manufacturer}
                </Text>
              </Flex>
            )}
          </Flex>
        );
      },
      shouldCellUpdate: (record, prevRecord) =>
        isEqual(prevRecord.key, record?.key),
    },
    {
      key: "sku",
      title: "SKU",
      width: 150,
      dataIndex: "sku",
      sorter: (a, b) => a.sku - b.sku,
      render: (sku, record) => {
        return (
          <Flex gap={12} vertical style={{ minWidth: 130 }}>
            <Flex gap={5}>
              <Text style={{ color: token.colorTextSecondary }}>{sku}</Text>
            </Flex>

            {record?.variations[0]?.mainCatalog?.sku && (
              <Flex gap={5}>
                <Text strong>
                  {record?.variations[0]?.mainCatalog?.sku ?? "-"}
                </Text>
              </Flex>
            )}
          </Flex>
        );
      },
      shouldCellUpdate: (record, prevRecord) =>
        isEqual(prevRecord.key, record?.key),
    },
    // El nombre del seller y el de xcons, cada uno en su columna (xcons-docs#630).
    {
      key: "sku",
      title: t("products.show.tableNameSeller"),
      width: 280,
      dataIndex: "name",
      sorter: (a, b) =>
        a.name.toLowerCase().charCodeAt(0) - b.name.toLowerCase().charCodeAt(0),
      render: (name) => (
        <Text style={{ color: token.colorTextSecondary, minWidth: 180 }}>
          {name}
        </Text>
      ),
      shouldCellUpdate: (record, prevRecord) =>
        isEqual(prevRecord.key, record?.key),
    },
    {
      key: "xcons",
      title: t("products.show.tableNameXcons"),
      width: 350,
      // Por el % de match guardado (xcons-docs#630). Los asociados no tienen sugerencia:
      // quedan al final de mayor a menor.
      sorter: (a, b) => (a.match?.score ?? -1) - (b.match?.score ?? -1),
      render: (_, record) => (
        <Flex gap={12} vertical style={{ minWidth: 200 }}>
          {record?.variations[0]?.mainCatalog?.name && (
            <Text strong style={{ color: token.colorPrimary }}>
              {record?.variations[0]?.mainCatalog?.name}
            </Text>
          )}
          {/* Sin asociar: la primera sugerencia del catálogo, para asociar desde acá
              (xcons-docs#609). */}
          {record?.variations?.length === 0 && (
            <SugerenciaDeAsociacion
              sellerCode={sellerCode}
              producto={record}
              onAsociado={mainTableRefecth}
            />
          )}
        </Flex>
      ),
      shouldCellUpdate: (record, prevRecord) =>
        isEqual(prevRecord.key, record?.key),
    },
    {
      key: "sku",
      title: t("products.show.tableUnit"),
      width: 200,
      dataIndex: "unit",
      render: (unit, record) => {
        return (
          <Flex gap={12}>
            {record?.variations[0]?.mainCatalog?.unit && (
              <Text strong>
                {record?.variations[0]?.mainCatalog?.unit ?? "-"}
              </Text>
            )}
          </Flex>
        );
      },
      shouldCellUpdate: (record, prevRecord) =>
        isEqual(prevRecord.key, record?.key),
    },
    {
      key: "sku",
      title: t("products.show.tablePrice"),
      width: 200,
      dataIndex: "price",
      sorter: (a, b) => a.price - b.price,
      render: (price, record) => {
        const taxRate = record.variations?.[0]?.mainCatalog?.tax ?? 0;
        const hasIva = taxRate > 0;
        const displayPrice = hasIva ? price * (1 + taxRate / 100) : price;
        return (
          <Flex align="center" gap={6} wrap={false} style={{ paddingLeft: "6px", whiteSpace: "nowrap" }}>
            <Text strong style={{ whiteSpace: "nowrap" }}>
              <NumberField
                value={displayPrice}
                options={{
                  style: "currency",
                  currency: "ARS",
                }}
              />
            </Text>
            <Tag
              color="default"
              style={{ marginInlineStart: 0, fontSize: 10, padding: "0 4px", lineHeight: "18px", flexShrink: 0 }}
            >
              {hasIva ? "c/IVA" : "s/IVA"}
            </Tag>
          </Flex>
        );
      },
      shouldCellUpdate: (record, prevRecord) =>
        isEqual(prevRecord?.key, record?.key),
    },
    {
      key: "sku",
      title: t("products.show.tableStock"),
      width: 100,
      dataIndex: "stock",
      sorter: (a, b) => (a.stock ?? 0) - (b.stock ?? 0),
      render: (stock) => {
        return (
          <Flex gap={12}>
            <Text strong>{stock}</Text>
          </Flex>
        );
      },
      shouldCellUpdate: (record, prevRecord) =>
        isEqual(prevRecord?.key, record?.key),
    },
    {
      key: "sku",
      title: t("products.show.tableLeadtime"),
      dataIndex: "leadTime",
      sorter: (a, b) => (a.leadTime ?? 0) - (b.leadTime ?? 0),
      render: (leadTime) => {
        return (
          <Flex gap={12} style={{ minWidth: 80 }}>
            <Text strong>
              {leadTime ?? "0"} {t("products.show.tableDays")}
            </Text>
          </Flex>
        );
      },
    },
    {
      key: "sku",
      title: t("products.show.tableDate"),
      sorter: (a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt),
      dataIndex: "updatedAt",
      render: (updatedAt, record) => {
        return (
          <Flex vertical gap={1} style={{ minWidth: 140 }}>
            <Text style={{ color: token.colorTextSecondary }}>
              {t("products.show.tableCreated")}{" "}
              {dayjs(record.createdAt).format(record?.dateFormat ?? "D/M/YY")}
            </Text>
            <Text style={{ color: token.colorTextSecondary }}>
              {t("products.show.tableUpdated")}{" "}
              {dayjs(updatedAt).format(record?.dateFormat ?? "D/M/YY")}
            </Text>
          </Flex>
        );
      },
      shouldCellUpdate: (record, prevRecord) =>
        isEqual(prevRecord.key, record.key),
    },
    {
      key: "sku",
      title: t("products.show.tableSale"),
      dataIndex: "state",
      sorter: (a, b) => {
        const aEnabled = a.variations[0]?.enabled ? 1 : 0;
        const bEnabled = b.variations[0]?.enabled ? 1 : 0;
        return aEnabled - bEnabled;
      },
      render: (state, record) => {
        const variations = record.variations[0] || [];
        return (
          <Flex gap={12} vertical style={{ whiteSpace: "nowrap" }}>
            <Flex gap={7}>
              <Badge
                // Inactivo en gris: es un estado, no un error (xcons-docs#634).
                status={variations.enabled === true ? "success" : "default"}
              />
              <span style={{ textTransform: "capitalize" }}>
                {variations.enabled === true
                  ? t("filter.products.labelStatusActive")
                  : t("filter.products.labelStatusDesactive")}
              </span>
            </Flex>
          </Flex>
        );
      },
      shouldCellUpdate: (record, prevRecord) =>
        isEqual(prevRecord.key, record.key),
    },
  ];

  /**
   * El orden de las columnas, guardado en el navegador de cada usuario como en Presupuestos
   * (xcons-docs#630). La de desplegar las variaciones no se mueve: queda segunda, donde estaba.
   */
  const { order: ordenDeColumnas, reorder: reordenarColumnas } = useColumnOrder({
    storageKey: "xcons:sellers:linkedProducts:columnOrder:v1",
    togglableKeys: COLUMNAS_DE_PRODUCTOS,
  });
  const sensoresDeArrastre = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  const alSoltarColumna = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      reordenarColumnas(String(active.id), String(over.id));
    }
  };
  const columnasPorId = Object.fromEntries(
    mainColumns
      .filter((c) => c !== Table.EXPAND_COLUMN)
      .map((c, i) => [
        COLUMNAS_DE_PRODUCTOS[i],
        {
          ...c,
          onHeaderCell: () => ({ columnKey: COLUMNAS_DE_PRODUCTOS[i] }) as any,
        },
      ]),
  );
  const columnasOrdenadas = ordenDeColumnas
    .map((id) => columnasPorId[id])
    .filter(Boolean);
  const columnasDeLaTabla: TableColumnsType<any> = [
    columnasOrdenadas[0],
    Table.EXPAND_COLUMN,
    ...columnasOrdenadas.slice(1),
  ];

  const expandedRowRender = (record: any) => {
    const columns: TableColumnsType<any> = [
      {
        key: "variations",
        title: t("products.show.tableProducts"),
        width: 150,
        dataIndex: "variations",
        render: (_, record) => {
          return (
            <>
              <Flex gap={12} style={{ height: 80, width: 80 }}>
                <img
                  style={{ width: 80, height: 80, objectFit: "contain" }}
                  src={
                    record?.mainCatalog?.image
                      ? record?.mainCatalog?.image
                      : "https://placehold.co/206x206?text=No+Image"
                  }
                  alt={record?.mainCatalog?.name}
                />
              </Flex>
            </>
          );
        },
        shouldCellUpdate: (record, prevRecord) =>
          isEqual(prevRecord.key, record.key),
      },
      {
        key: "manufacturer",
        title: t("products.show.tableBrand"),
        width: 150,
        dataIndex: "manufacturer",
        render: (manufacturer, record) => {
          return (
            <Flex gap={12} vertical style={{ minWidth: 130 }}>
              <Flex gap={5}>
                <Text strong style={{ color: token.colorTextSecondary }}>
                  {record?.mainCatalog?.manufacturer}
                </Text>
              </Flex>
            </Flex>
          );
        },
        shouldCellUpdate: (record, prevRecord) =>
          isEqual(prevRecord.key, record.key),
      },
      {
        key: "sku",
        title: "SKU",
        width: 150,
        dataIndex: "sku",
        render: (sku, record) => {
          return (
            <Flex gap={12} vertical style={{ minWidth: 130 }}>
              <Flex gap={5}>
                <Text strong>{record.mainCatalog?.sku}</Text>
              </Flex>
            </Flex>
          );
        },
        shouldCellUpdate: (record, prevRecord) =>
          isEqual(prevRecord.key, record.key),
      },
      {
        key: "name",
        title: t("products.show.tableName"),
        width: 350,
        dataIndex: "name",
        sorter: (a, b) => Date.parse(a.last_update) - Date.parse(b.last_update),
        render: (name, record) => {
          return (
            <Flex gap={12} vertical style={{ minWidth: 200 }}>
              <Flex gap={5}>
                <Text strong style={{ color: token.colorTextSecondary }}>
                  {record.mainCatalog?.name}
                </Text>
              </Flex>
            </Flex>
          );
        },
        shouldCellUpdate: (record, prevRecord) =>
          isEqual(prevRecord.key, record.key),
      },
      {
        key: "unit",
        title: t("products.show.tableUnit"),
        width: 120,
        dataIndex: "unit",
        render: (unit, record) => {
          return (
            <Flex gap={12}>
              <Text strong>{record?.mainCatalog?.unit ?? "-"}</Text>
            </Flex>
          );
        },
        shouldCellUpdate: (record, prevRecord) =>
          isEqual(prevRecord.key, record?.key),
      },
    ];

    const data = record?.variations;
    return (
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        rowKey="sku"
      />
    );
  };

  const {
    tableQuery: { data: dataXlsTable, refetch: refetchTableXls },
    tableProps: tablePropsXlsTable,
  } = useTable<HttpError>({
    resource: `sellers/linkedproducts/import/${sellerCode}`,
    // Cada panel se queda con las de su origen (xcons-docs#609): con las 10 de antes, el de
    // Excel podía quedar casi vacío detrás de las sincronizaciones del ERP.
    pagination: { pageSize: 100 },
    // Vive en un panel: no lee ni escribe la URL, que es de la tabla de productos. Leía el
    // `pageSize=10` que dejaba ahí ella misma.
    syncWithLocation: false,
  });

  /**
   * Función que se llama, no componente: un componente declarado acá adentro es otro en cada
   * render, y React lo desmontaba entero —con los paneles de Excel e Integrar abiertos— cada vez
   * que la tabla se refrescaba, por ejemplo al subir un Excel (xcons-docs#609).
   */
  const encabezadoDeLaTabla = () => (
    <>
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="main_search" style={{ marginBottom: 0 }}>
            <Search
              placeholder={t("products.show.search")}
              allowClear
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    main_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>
        <Flex
          gap="large"
          justify="center"
          style={{ width: screens.sm ? "auto" : "100%" }}
        >
          <LateralModal
            key="filters-modal"
            buttonName={t("buttons.filters")}
            title={t("filter.products.title")}
          >
            <ProductsFilters
              searchFormProps={searchFormProps}
              filters={filters}
              t={t}
              matchPending={(data?.meta as any)?.matchPending}
            />
          </LateralModal>

          {/* Excel e Integrar, separados (xcons-docs#609). Excel queda en un ícono para que el
              que se vea sea Integrar: es la forma que queremos que usen los sellers. */}
          {botonesDeCarga()}
        </Flex>
      </Flex>

      {/* Asociado / No asociado sin abrir el panel de filtros (xcons-docs#609). Es el mismo
          filtro `status` del panel. Activo se ve como un chip marcado, y por eso no se repite
          como etiqueta de filtro: las demás siguen en la misma línea. */}
      <Flex
        className="filtros-sugeridos"
        align="center"
        gap={8}
        wrap="wrap"
        style={{ marginTop: 12, marginBottom: 16 }}
      >
        <Text style={{ fontSize: 13, color: token.colorTextSecondary }}>
          Filtros sugeridos:
        </Text>
        {/* Los mismos botones que el filtro del panel (xcons-docs#609), no pastillas: Todos
            lo saca. */}
        <Radio.Group
          size="small"
          value={estadoFiltrado ?? ""}
          onChange={(e) => filtrarPorEstado(e.target.value || undefined)}
        >
          <Radio.Button value="">{t("filter.calendar.all")}</Radio.Button>
          {FILTROS_SUGERIDOS.map((f) => (
            <Radio.Button key={f.value} value={f.value}>
              {t(f.label)}
            </Radio.Button>
          ))}
        </Radio.Group>
        <Divider type="vertical" style={{ height: 20, margin: "0 4px" }} />
        <Radio.Group
          size="small"
          value={tramoSugerido ?? ""}
          onChange={(e) => filtrarPorMatch(e.target.value || undefined)}
        >
          <Radio.Button value="">{t("filter.calendar.all")}</Radio.Button>
          {opcionesDeMatch(t)
            .filter((o) => o.value !== "personalizado")
            .map((o) => (
              <Radio.Button key={o.value} value={o.value}>
                {o.label}
              </Radio.Button>
            ))}
        </Radio.Group>
        <FilterTags
          searchFormProps={searchFormProps}
          onSearch={onSearch}
          i18n={i18n}
          filters={filters?.filter((f: any) => !marcadoArriba(f))}
          // Quitar otra etiqueta reemplaza los filtros por los que ve: los marcados arriba, que
          // no ve, se vuelven a sumar para no perderlos.
          setFilters={(nuevos: CrudFilters, modo?: "merge" | "replace") =>
            setFilters(
              modo === "replace"
                ? [
                    ...nuevos,
                    ...(filters?.filter(marcadoArriba) ?? []),
                  ]
                : nuevos,
              modo,
            )
          }
          t={t}
        />
      </Flex>

      {/* <Text color={token.colorTextSecondary} style={{display: 'block', marginBottom: showSelectedOption ? 12 : 0}}>
        {t("products.show.subTitle", {linked_products: 100, stores: 12, total: 182})}
      </Text> */}

      <Flex gap={12} align="center">
        {showSelectedOption && (
          <>
            <Text color={token.colorTextSecondary}>
              {selectedRowOnTable?.length} {t("actions.selected")}
            </Text>
            {/* El checkbox de la cabecera elige sólo la página: el resto, a la vista y no
                escondido en su menú (xcons-docs#624). */}
            {(data?.total ?? 0) > selectedRowOnTable.length && (
              <Typography.Link onClick={seleccionarTodos}>
                Seleccionar los {data?.total}
              </Typography.Link>
            )}
            <Select
              style={{ width: 320 }}
              placeholder={t("actions.choose")}
              // Siempre vacío: es un menú de acciones, no un valor. Si quedara la elegida,
              // cancelar su confirmación y volver a elegirla no dispararía `onChange`.
              value={null}
              onChange={handleChange}
              // Al abrirse vuelve a mirar las sugerencias: se cargan de a poco y Descartar
              // las agota, así que lo que valía al seleccionar puede haber cambiado.
              onDropdownVisibleChange={(abierto) =>
                abierto && setRevisarSugerencias((n) => n + 1)
              }
              options={[
                // "Manualmente" para separarla de la sugerida (xcons-docs#609): abre el
                // asociador y se elige a mano.
                { value: "link", label: "Asociar manualmente" },
                {
                  value: "suggestion",
                  label: "Asociar con la sugerencia",
                  disabled: !hayConSugerencia,
                },
                { value: "export", label: t("actions.selectExport") },
                { value: "enable", label: t("actions.activate") },
                { value: "disable", label: t("actions.desactivate") },
                { value: "unlink", label: t("actions.eraseProduct") },
                { value: "delete", label: t("actions.deleteProduct") },
              ]}
            />
          </>
        )}
      </Flex>
    </>
  );

  const handleChange = (value: string) => {
    switch (value) {
      case "link":
        abrirAsociador(selectedRowOnTable);
        break;
      case "suggestion":
        asociarConLaSugerencia();
        break;
      case "export":
        downloadSelectedData();
        break;
      case "enable":
        handleMassAction(value);
        break;
      case "disable":
        handleMassAction(value);
        break;
      case "unlink":
        handleMassAction(value);
        break;
      case "delete":
        handleDeleteMassAction(value);
        break;
      default:
        break;
    }
  };

  /**
   * Los SKUs de catálogo (xcons) de las filas seleccionadas, más las filas que no aportaron
   * ninguno.
   *
   * El SKU vive en `variation.mainCatalog`, que el listado resuelve con un leftJoin contra
   * `xcons_catalog` — una tabla materializada por un job, no una vista. Si la oferta apunta a
   * un producto que todavía no está ahí, `mainCatalog` viene null; y una fila sin vincular
   * directamente no tiene `variations`. Recorrer eso sin `?.` tiraba un TypeError **adentro
   * del try**, así que el usuario veía "Error en realizar enable" sin que saliera un solo
   * request: una fila rota anulaba la acción sobre las otras 19.
   */
  const collectMainCatalogSkus = (rows: any[]) => {
    const skus: string[] = [];
    const skipped: string[] = [];

    for (const row of rows ?? []) {
      const rowSkus: string[] = (row?.variations ?? [])
        .map((variation: any) => variation?.mainCatalog?.sku)
        .filter(Boolean);

      if (rowSkus.length) {
        skus.push(...rowSkus);
      } else if (row?.sku) {
        skipped.push(row.sku);
      }
    }

    return { skus, skipped };
  };

  /** Las filas descartadas se nombran, si no el usuario no sabe qué quedó afuera. */
  const notifySkippedRows = (skipped: string[]) => {
    if (!skipped.length) return;

    const shown = skipped.slice(0, 10).join(", ");
    notification.warning({
      message: t("message.massActionSkipped", { count: skipped.length }),
      description: skipped.length > 10 ? `${shown}…` : shown,
    });
  };

  const apiErrorDetail = (error: any) =>
    error?.response?.data?.message || error?.message || undefined;

  // "Seleccionar todos" puede traer cientos de filas: se manda de a tandas para no depender
  // de un solo request largo.
  const MASS_ACTION_CHUNK_SIZE = 200;

  const handleMassAction = async (action: string) => {
    const { skus, skipped } = collectMainCatalogSkus(selectedRowOnTable);

    if (skus.length === 0) {
      notification.info({ message: t("message.massActionNoAction") });
      notifySkippedRows(skipped);
      return;
    }

    try {
      for (let i = 0; i < skus.length; i += MASS_ACTION_CHUNK_SIZE) {
        await axiosInstance.post(
          `${API_URL_LINKED_SEARCH}/massAction/mainCatalog/${sellerCode}/action/${action}`,
          { mainCatalogSkus: skus.slice(i, i + MASS_ACTION_CHUNK_SIZE) },
        );
      }
      notification.success({ message: t("message.massActionSuccess") });
      notifySkippedRows(skipped);
      setSelectedRowOnTable([]);
      setSelectedRowKeys([]);
      mainTableRefecth();
    } catch (error) {
      notification.error({
        message: t("message.massActionError", { action }),
        description: apiErrorDetail(error),
      });
    }
  };

  const handleDeleteMassAction = async (action: string) => {
    const sellerSkus: string[] = (selectedRowOnTable as any[])
      .map((item: any) => item?.sku)
      .filter(Boolean);

    if (sellerSkus.length === 0) {
      notification.info({ message: t("message.massActionNoAction") });
      return;
    }

    try {
      for (let i = 0; i < sellerSkus.length; i += MASS_ACTION_CHUNK_SIZE) {
        await axiosInstance.post(
          `${API_URL_LINKED_SEARCH}/massAction/sellerProduct/${sellerCode}/action/${action}`,
          { sellerSkus: sellerSkus.slice(i, i + MASS_ACTION_CHUNK_SIZE) },
        );
      }
      notification.success({ message: t("message.massActionSuccess") });
      setSelectedRowOnTable([]);
      setSelectedRowKeys([]);
      mainTableRefecth();
    } catch (error) {
      notification.error({
        message: t("message.massActionError", { action }),
        description: apiErrorDetail(error),
      });
    }
  };

  const downloadSelectedData = async () => {
    const allData = await selectedRowOnTable;

    if (allData.length === 0) {
      message.error(t("message.noDataFound"));
      return;
    }

    const transformedData = allData.map((item: any) => {
      const skus = (item?.variations ?? [])
        .map((v: any) => v?.mainCatalog?.sku)
        .filter(Boolean)
        .join(", ");

      return {
        ...item,
        variations: item?.variations?.length ?? 0,
        variationsSkus: skus,
      };
    });

    const documentName = `${sellerCode}_xcons_${new Date().toISOString()}.xlsx`;
    const worksheet = utils.json_to_sheet(transformedData, {
      header: Object.keys(transformedData[0]),
    });
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Xcons data");

    XLSX.writeFile(workbook, documentName);

    message.success(t("message.archiveDownload"));
    setSelectedRowOnTable([]);
    setSelectedRowKeys([]);
  };

  const HeaderContent = React.memo(() => (
    <>
      <Flex justify="space-between">
        {/* El ícono del menú, como el resto de los títulos de página (xcons-docs#509). */}
        <Space align="center" style={{ marginBottom: 12 }}>
          <ApiOutlined style={{ fontSize: 22 }} />
          <Title level={3} style={{ margin: 0 }}>
            {t("smartMenu.label.linked")}
          </Title>
        </Space>
        {/* <Text strong type="secondary">
          {t("permissions")} <LockOutlined />
        </Text> */}
      </Flex>
    </>
  ));

  /**
   * El asociador es una pantalla (xcons-docs#609). Se le pasa la cola de productos a asociar:
   * la fila que se tocó, o los seleccionados con la acción masiva Asociar. Con más de uno,
   * Guardar avanza al siguiente.
   */
  const abrirAsociador = (cola: any[]) => {
    const conSku = cola.filter((p) => p?.sku);
    if (!conSku.length) return;
    const estado: EstadoDelAsociador = { cola: conSku, indice: 0 };
    navigate(
      `/sellers/linked-products/${sellerParam}/${encodeURIComponent(conSku[0].sku)}`,
      { state: estado },
    );
  };

  /**
   * Si alguno de los seleccionados tiene una sugerencia a la vista. Sin ninguno, la acción
   * masiva aparece deshabilitada. `revisarSugerencias` sólo fuerza a recalcularlo al abrir el
   * menú, porque las sugerencias viven fuera de React.
   */
  const [revisarSugerencias, setRevisarSugerencias] = useState(0);
  const hayConSugerencia = useMemo(
    () =>
      (selectedRowOnTable as any[]).some(
        (p) =>
          p?.variations?.length === 0 &&
          !!(sugerenciaActual(sellerCode, p.sku) ?? p?.match?.suggestedSku),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRowOnTable, sellerCode, revisarSugerencias],
  );

  /**
   * Acción masiva **Asociar con la sugerencia** (xcons-docs#609): cada seleccionado sin
   * asociar, con la sugerencia que muestra su celda —la que quedó después de Descartar—, o la
   * primera si todavía no se pidió. Pregunta antes, con cuántos quedan afuera y por qué.
   */
  const asociarConLaSugerencia = async () => {
    // Las filas seleccionadas: el estado se tipó como `Key[]`, pero guarda los productos.
    const sinAsociar = (selectedRowOnTable as any[]).filter(
      (p) => p?.variations?.length === 0,
    );
    message.loading({ content: "Buscando sugerencias…", key: "sugerencias" });
    const conSugerencia: Array<{ producto: any; catalogo: any }> = [];
    for (const producto of sinAsociar) {
      // La que muestra la fila si ya se pidió (Descartar la pudo cambiar); si no, la guardada
      // (xcons-docs#624), sin buscar: con "Seleccionar todos" serían cientos de búsquedas.
      const vista = sugerenciaActual(sellerCode, producto.sku);
      if (vista) {
        conSugerencia.push({ producto, catalogo: vista });
        continue;
      }
      if (producto.match?.suggestedSku) {
        conSugerencia.push({
          producto,
          catalogo: {
            sku: producto.match.suggestedSku,
            matchScore: producto.match.score ?? undefined,
          },
        });
        continue;
      }
      try {
        await pedirSugerencias(sellerCode, producto);
      } catch {
        // Sin sugerencia: queda afuera, y se cuenta abajo.
      }
      const catalogo = sugerenciaActual(sellerCode, producto.sku);
      if (catalogo) conSugerencia.push({ producto, catalogo });
    }
    message.destroy("sugerencias");
    const yaAsociados = selectedRowOnTable.length - sinAsociar.length;
    const sinOpcion = sinAsociar.length - conSugerencia.length;
    if (!conSugerencia.length) {
      message.info("Ninguno de los seleccionados tiene una sugerencia para asociar");
      return;
    }
    Modal.confirm({
      title: `¿Asociar ${conSugerencia.length} producto${
        conSugerencia.length !== 1 ? "s" : ""
      } con su sugerencia?`,
      content: (
        <Flex vertical gap={4}>
          <Text>Cada uno se asocia con la sugerencia que muestra su fila.</Text>
          {yaAsociados > 0 && (
            <Text type="secondary">
              {yaAsociados} ya estaba{yaAsociados !== 1 ? "n" : ""} asociado
              {yaAsociados !== 1 ? "s" : ""}: no se toca{yaAsociados !== 1 ? "n" : ""}.
            </Text>
          )}
          {sinOpcion > 0 && (
            <Text type="secondary">
              {sinOpcion} no tiene{sinOpcion !== 1 ? "n" : ""} sugerencia: queda
              {sinOpcion !== 1 ? "n" : ""} sin asociar.
            </Text>
          )}
        </Flex>
      ),
      okText: t("buttons.link"),
      cancelText: t("buttons.cancel"),
      onOk: async () => {
        // Una sola llamada para todo el lote (xcons-docs#611): la API asocia lo que puede y
        // dice por qué no cada uno de los otros.
        let resultados: ResultadoDeSugerencia[] = [];
        try {
          resultados = await asociarConSugerencias(
            sellerCode,
            conSugerencia.map(({ producto, catalogo }) => ({
              sellerSku: producto.sku,
              catalogo,
            })),
          );
        } catch {
          message.error("No se pudo asociar");
          return;
        }
        const asociados = resultados.filter((r) => r.ok).length;
        const fallidos = resultados.filter((r) => !r.ok);
        if (asociados) message.success(`${asociados} asociado${asociados !== 1 ? "s" : ""}`);
        if (fallidos.length)
          Modal.warning({
            title:
              fallidos.length === 1
                ? "1 no se pudo asociar"
                : `${fallidos.length} no se pudieron asociar`,
            content: (
              // Con "Seleccionar todos" pueden ser cientos: la lista scrollea y el botón queda a
              // la vista.
              <Flex vertical gap={4} style={{ maxHeight: "60vh", overflowY: "auto" }}>
                {fallidos.map((r) => (
                  <Text key={r.sellerSku}>
                    <Text strong>{r.sellerSku}</Text>: {motivoSinAsociar(r)}
                  </Text>
                ))}
              </Flex>
            ),
          });
        setSelectedRowKeys([]);
        setSelectedRowOnTable([]);
        // Los asociados salen del filtro y las páginas se achican: volver a la primera, o se
        // queda en una página que ya no existe y la tabla aparece vacía (xcons-docs#624).
        if (current !== 1) setCurrent(1);
        else mainTableRefecth();
        // Y el aviso "Encontramos N…" del historial baja con lo que se acaba de asociar.
        if (asociados) refetchTableXls();
      },
    });
  };

  const FILTROS_SUGERIDOS = [
    { value: "linked", label: "filter.products.optionLinked" },
    { value: "unlinked", label: "filter.products.optionUnlinked" },
  ];
  const estadoFiltrado = filters?.find(
    (f: any) => f.field === "product.status",
  )?.value;
  // Tocar el activo lo saca, como las pills del Historial. Va directo a la tabla y no por el
  // submit del form: con el panel de filtros cerrado el campo `status` no está montado, y el
  // submit lo descarta.
  const filtrarPorEstado = (value?: string) => {
    searchFormProps.form?.setFieldsValue({ status: value });
    setFilters([{ field: "product.status", operator: "eq", value }]);
  };

  // Los tres tramos del semáforo, también sin abrir el panel (xcons-docs#634). Un rango elegido
  // a mano en el panel no tiene botón: ahí no se marca ninguno y sigue su etiqueta.
  const { tramo: tramoAplicado } = tramoDelFiltro(
    filters?.find((f: any) => f.field === "matchScore" && f.operator === "between")
      ?.value,
  );
  const tramoSugerido = tramoAplicado !== "personalizado" ? tramoAplicado : undefined;
  const filtrarPorMatch = (value?: TramoDeMatch) => {
    searchFormProps.form?.setFieldsValue({
      match_tramo: value,
      match_desde: undefined,
      match_hasta: undefined,
    });
    setFilters(filtrosDeMatch(value));
  };
  // Lo que ya se ve marcado arriba no se repite como etiqueta.
  const marcadoArriba = (f: any) =>
    f.field === "product.status" || (!!tramoSugerido && f.field === "matchScore");

  const botonesDeCarga = () => (
    <>
      <Tooltip title="Importar desde Excel">
        <span style={{ display: "inline-block" }}>
          <LateralModal
            // Cambiar la key lo cierra: el link del aviso de coincidencias vuelve a la tabla.
            key={`excel-modal-${cierresDelImport}`}
            maskClosable={false}
            // Al ancho del form de dos columnas, no a la mitad de la pantalla.
            width={720}
            buttonType="default"
            iconSvg={<ExcelIcon style={{ fontSize: 20 }} />}
            // Con texto (xcons-docs#609): el ícono solo no decía qué hace. Integrar sigue
            // siendo el principal: va en verde y en negrita.
            buttonName="Importar"
            buttonShape="default"
            title={t("products.show.lateralModalTitle")}
          >
            <ImportProducts
              refetchFormXls={refetchFormXls}
              refetchTableXls={refetchTableXls}
              sellerCode={sellerCode}
              dataEmpty={dataEmpty}
              configXlsData={configXlsData}
              dataXlsTable={dataXlsTable}
              tablePropsXlsTable={tablePropsXlsTable}
              isLoading={importLoading}
              onAsociarCoincidencias={asociarCoincidencias}
            />
          </LateralModal>
        </span>
      </Tooltip>

      <LateralModal
        key="integrar-modal"
        maskClosable={false}
        // El mismo ancho que el de Excel.
        width={720}
        buttonType="primary"
        iconSvg={<CloudSyncOutlined />}
        buttonShape="default"
        buttonName="Integrar"
        strong
        title="Integrar tu ERP"
      >
        <IntegrarErp
          sellerCode={sellerCode}
          onSincronizado={mainTableRefecth}
          importaciones={dataXlsTable}
          tablePropsImportaciones={tablePropsXlsTable}
          refetchImportaciones={refetchTableXls}
          onAsociarCoincidencias={asociarCoincidencias}
        />
      </LateralModal>
    </>
  );

  if (dataEmpty) {
    return (
      <>
        <HeaderContent />
        <EmptyFormCard
          message={t("products.title.sheetEmpty")}
          loading={isFetching}
        >
          <Flex gap="large" justify="center">
            {botonesDeCarga()}
          </Flex>
        </EmptyFormCard>
      </>
    );
  }

  return (
    <>
      <HeaderContent />
      {/* Las columnas se mueven con el asa (xcons-docs#630). */}
      <DndContext
        sensors={sensoresDeArrastre}
        collisionDetection={closestCenter}
        onDragEnd={alSoltarColumna}
      >
        <SortableContext
          items={ordenDeColumnas}
          strategy={horizontalListSortingStrategy}
        >
          <Table
            components={COMPONENTES_DE_LA_TABLA}
            {...tableProps}
            ref={tblRef}
            style={{ cursor: "pointer" }}
            scroll={{ x: "max-content", y: 55 * 10.5 }}
            // virtual
            title={encabezadoDeLaTabla}
            rowKey="sku"
            dataSource={dataSource ?? []}
            columns={columnasDeLaTabla}
            loading={isFetching}
            rowSelection={rowSelection ? rowSelection : undefined}
            onRow={(record) => ({
              onClick: () => {
                abrirAsociador([record]);
              },
            })}
            expandable={{
              expandedRowRender: expandedRowRender,
              rowExpandable: (record) => {
                return record.variations && record.variations.length > 1;
              },
            }}
            locale={{
              triggerAsc: t("table.triggerAsc"),
              triggerDesc: t("table.triggerDesc"),
              cancelSort: t("table.cancelSort"),
            }}
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              pageSizeOptions: OPCIONES_POR_PAGINA,
              showQuickJumper: false,
              showTotal: (total) => (
                <PaginationTotal
                  lastUpdate={formatLastUpdate(dataSource[0]?.updatedAt)}
                  total={total}
                  entityName="products"
                />
              ),
            }}
          />
        </SortableContext>
      </DndContext>
    </>
  );
};
